// Annotation Portal React UI for Manage Super_Admin

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import AddAdmin from "layouts/super_admin/admin/modals/AddAdmin";
import AddAnnotator from "layouts/super_admin/admin/modals/AddAnnotator";
import EditUser from "layouts/super_admin/admin/modals/EditUser";
import ResetPassword from "layouts/super_admin/admin/modals/ResetPassword";
import DeleteUser from "layouts/super_admin/admin/modals/DeleteUser";
import UsersDataTable from "layouts/super_admin/admin/data/UsersDataTable";
import DeletedUsersDataTable from "layouts/super_admin/deleted_users/data/DeletedUsersDataTable";
import { getUsers, getAllUsersIncludingDeleted, getAllDeletedUsers } from "services/userService";
import AppSnackbar from "layouts/notifications/Snackbar";
import { useTranslation } from "hooks/useTranslation";
import { useLanguage } from "context/LanguageContext";
import { useTableSelectionModals } from "hooks/useTableSelectionModals";
import { getRoleLabel } from "utils/roleLabels";

function ManageSuper_AdminContent() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [openAddAdmin, setOpenAddAdmin] = useState(false);
  const [openAddAnnotator, setOpenAddAnnotator] = useState(false);
  const {
    selectedItem: selectedUserId,
    openEdit,
    openReset,
    openDelete,
    handleEdit,
    handleReset,
    handleDelete,
    handleCloseDelete,
    handleCloseEdit,
    handleCloseReset,
    onPaginationChange,
  } = useTableSelectionModals();
  const [userList, setUserData] = useState([]);
  const [deletedUserList, setDeletedUserData] = useState([]);
  const [activeTab, setActiveTab] = useState(0); // 0 for Active Users, 1 for Deleted Users

  // Filter states
  const [filterRole, setFilterRole] = useState("");
  const [filterDistrict, setFilterDistrict] = useState("");
  const [filteredUserList, setFilteredUserList] = useState([]);
  const [filteredDeletedUserList, setFilteredDeletedUserList] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, type: "", message: "" });

  const showSnackbar = (type, message) => {
    setSnackbar({ open: true, type, message });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false, type: "", message: "" }));
  };

  /* fetch users data to show users datatable */
  const fetchUsersData = async () => {
    const data = await getUsers();
    const allUsers = await getAllUsersIncludingDeleted();

    // As we want to show created_by as name therefore need to have modified getUsers() response
    let modifiedData = [];

    data.forEach((item) => {
      let user = allUsers.find((elem) => elem?.id == item?.created_by);
      item.created_by = `${user?.first_name ?? "-"} ${user?.last_name ?? "-"}`;
      modifiedData.push(item);
    });

    setUserData(modifiedData);
    setFilteredUserList(modifiedData);
  };

  /* fetch deleted users data to show in datatable */
  const fetchDeletedUsersData = async () => {
    try {
      const data = await getAllDeletedUsers();
      const allUsers = await getAllUsersIncludingDeleted();

      // As we want to show deleted_by as name
      let modifiedData = [];

      data.forEach((item) => {
        let created_by_user = allUsers.find((elem) => elem?.id == item?.created_by);
        let deleted_by_user = allUsers.find((elem) => elem?.id == item?.deleted_by);
        item.created_by = `${created_by_user?.first_name ?? "-"} ${
          created_by_user?.last_name ?? "-"
        }`;
        item.deleted_by = `${deleted_by_user?.first_name ?? "-"} ${
          deleted_by_user?.last_name ?? "-"
        }`;
        modifiedData.push(item);
      });

      setDeletedUserData(modifiedData);
      setFilteredDeletedUserList(modifiedData);
    } catch (error) {
      console.error("Error fetching deleted users:", error);
    }
  };

  /* useEffect hook call when component get mount */
  useEffect(() => {
    fetchUsersData();
    fetchDeletedUsersData();
  }, []);

  /* Handle tab change */
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Reset filters when switching tabs
    handleFilterReset();
  };

  // Handle filter submission
  const handleFilterSubmit = () => {
    const sourceList = activeTab === 0 ? userList : deletedUserList;
    let filtered = sourceList;

    if (filterRole) {
      filtered = filtered.filter((user) => user.role === filterRole);
    }
    if (filterDistrict) {
      filtered = filtered.filter((user) => user.district === filterDistrict);
    }

    if (activeTab === 0) {
      setFilteredUserList(filtered);
    } else {
      setFilteredDeletedUserList(filtered);
    }
  };

  // Handle filter reset
  const handleFilterReset = () => {
    setFilterRole("");
    setFilterDistrict("");
    setFilteredUserList(userList);
    setFilteredDeletedUserList(deletedUserList);
  };

  // Get unique values for filters based on active tab
  const currentList = activeTab === 0 ? userList : deletedUserList;
  const uniqueRoles = [...new Set(currentList.map((user) => user.role))].filter(Boolean);
  const uniqueDistricts = [...new Set(currentList.map((user) => user.district))].filter(Boolean);

  // Get columns and rows based on active tab
  const activeUsersTable = UsersDataTable({
    users: filteredUserList,
    onEdit: handleEdit,
    onReset: handleReset,
    onDelete: handleDelete,
    t: t,
  });

  const deletedUsersTable = DeletedUsersDataTable({
    users: filteredDeletedUserList,
    t: t,
    language,
  });

  const { columns, rows } = activeTab === 0 ? activeUsersTable : deletedUsersTable;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={1} mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center">
              <MDTypography variant="h6">{t("superAdmin.title")}</MDTypography>
              {activeTab === 0 && (
                <MDBox display="flex" justifyContent="end" alignItems="center" gap="10px">
                  <MDButton variant="gradient" color="info" onClick={() => setOpenAddAdmin(true)}>
                    <Icon>add</Icon>&nbsp;{t("superAdmin.buttons.addAdmin")}
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => setOpenAddAnnotator(true)}
                  >
                    <Icon>add</Icon>&nbsp;{t("superAdmin.buttons.addAnnotator")}
                  </MDButton>
                </MDBox>
              )}
            </MDBox>

            {/* Tabs Section */}
            <MDBox mt={3}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                sx={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  boxShadow: 1,
                  "& .MuiTab-root": {
                    minHeight: "48px",
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: "#7b809a",
                    "&.Mui-selected": {
                      color: "#1976d2",
                      fontWeight: 600,
                    },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1976d2",
                    height: "3px",
                  },
                }}
              >
                <Tab label={t("superAdmin.tabs.activeUsers", "Active Users")} />
                <Tab label={t("superAdmin.tabs.deletedUsers", "Deleted Users")} />
              </Tabs>
            </MDBox>

            {/* Filter Section */}
            <MDBox mt={3} mb={2}>
              <Card sx={{ p: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id="role-filter-label">{t("superAdmin.filters.role")}</InputLabel>
                      <Select
                        labelId="role-filter-label"
                        value={filterRole}
                        label={t("superAdmin.filters.role")}
                        onChange={(e) => setFilterRole(e.target.value)}
                        sx={{
                          height: "56px",
                        }}
                      >
                        <MenuItem value="">
                          <em>{t("superAdmin.filters.all")}</em>
                        </MenuItem>
                        {uniqueRoles.map((role) => (
                          <MenuItem key={role} value={role}>
                            {getRoleLabel(role, t)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth>
                      <InputLabel id="district-filter-label">
                        {t("superAdmin.filters.district")}
                      </InputLabel>
                      <Select
                        labelId="district-filter-label"
                        value={filterDistrict}
                        label={t("superAdmin.filters.district")}
                        onChange={(e) => setFilterDistrict(e.target.value)}
                        sx={{
                          height: "56px",
                        }}
                      >
                        <MenuItem value="">
                          <em>{t("superAdmin.filters.all")}</em>
                        </MenuItem>
                        {uniqueDistricts.map((district) => (
                          <MenuItem key={district} value={district}>
                            {district}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <MDButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      onClick={handleFilterSubmit}
                    >
                      {t("superAdmin.buttons.applyFilters")}
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <MDButton
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      onClick={handleFilterReset}
                    >
                      {t("superAdmin.buttons.reset")}
                    </MDButton>
                  </Grid>
                </Grid>
              </Card>
            </MDBox>

            <MDBox pt={3} pb={3}>
              <Card
                sx={{
                  boxShadow: 4,
                  borderRadius: 2,
                  overflow: "visible",
                  backgroundColor: "#f9fafe",
                  border: "1px solid #e0e0e0",
                }}
              >
                <MDBox>
                  <DataTable
                    key={activeTab}
                    table={{ columns, rows }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 5, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    canSearch={true}
                    pagination={{ variant: "gradient", color: "info" }}
                    noEndBorder={false}
                    onPaginationChange={onPaginationChange}
                  />
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Modals */}
      <AddAdmin
        open={openAddAdmin}
        onClose={() => setOpenAddAdmin(false)}
        onSuccess={() => {
          fetchUsersData();
          fetchDeletedUsersData();
        }}
        title={t("superAdmin.modals.addAdmin")}
      />
      <AddAnnotator
        open={openAddAnnotator}
        onClose={() => setOpenAddAnnotator(false)}
        onSuccess={() => {
          fetchUsersData();
          fetchDeletedUsersData();
        }}
        title={t("superAdmin.modals.addAnnotator")}
      />
      {selectedUserId && (
        <EditUser
          open={openEdit}
          onClose={handleCloseEdit}
          user={selectedUserId}
          onSuccess={(message) => {
            fetchUsersData();
            fetchDeletedUsersData();
            if (message) showSnackbar("success", message);
          }}
          title={t("superAdmin.modals.editUser")}
          successMessageKey="superAdmin.modals.success.userUpdated"
        />
      )}
      <ResetPassword
        open={openReset}
        onClose={handleCloseReset}
        user={selectedUserId}
        onSuccess={() => {
          fetchUsersData();
          fetchDeletedUsersData();
        }}
        title={t("superAdmin.modals.resetPassword")}
      />
      <DeleteUser
        key={selectedUserId?.id ?? "closed"}
        open={openDelete}
        onClose={handleCloseDelete}
        user={selectedUserId}
        onSuccess={(message) => {
          fetchUsersData();
          fetchDeletedUsersData();
          if (message) showSnackbar("success", message);
        }}
        title={t("superAdmin.modals.deleteUser")}
      />
      <AppSnackbar
        type={snackbar.type}
        message={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
      />
    </DashboardLayout>
  );
}

// Main component with Language Provider
function ManageSuper_Admin() {
  return <ManageSuper_AdminContent />;
}

export default ManageSuper_Admin;
